import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { Button, Input, Modal, Select, Checkbox } from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import TextArea from 'antd/es/input/TextArea'

import Loader from '../../pages/pages/loader'
import CustomersDropdown from '../CustomersDropdown'
import IspDropdown from '../IspDropdown'
import IptvStatusDropdown from '../IptvStatusDropdown'
import IptvAppDropdown from '../IptvAppDropdown'
import CampaignDropdown from '../CampaignDropdown'

import { getUserId } from '../../helpers/auth'
import {
  useCreateIptvServerMutation,
  useUpdateIptvServerMutation,
  useMarkIptvContentRemovedMutation,
  useLazyGetIptvServerByIdQuery
} from '../../redux/apis/linkManagement/iptv/server/lmIptvServerApiSlice'
import { useCalculateIpAddressQuery } from '../../redux/apis/domainsManagement/domains/dMDomainsApiSlice'
import CustomerChannelsDropdown from '../CustomerChannelsDropdown'

const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(handler)
  }, [value, delay])
  return debouncedValue
}

const buildValues = (record, state) => ({
  iptv: record?.iptv?.id || '',
  domain: record?.domain || '',
  ip_address: record?.ip_address?.ip_address || '',
  status: state === 'edit' ? record?.status?.id : 1,
  extra_info: record?.extra_info || '',
  insertion_type: record?.domain ? 'domain' : record?.ip_address ? 'ip' : '',
  customers: record?.customers?.map(c => c.id) || [],
  takedown_channels: record?.takedown_channels || false,
  takedown_campaigns: record?.takedown_campaigns || false,
  channels: record?.channels || [{ channel_id: null, url: '' }],
  campaigns: record?.campaigns
    ? record.campaigns.map(camp => ({
        campaign_id: camp.campaign_id,
        urls: Array.isArray(camp.urls) ? camp.urls.join('\n') : camp.urls || ''
      }))
    : [{ campaign_id: null, urls: '' }],
  real_isp_name: record?.real_isp_name || null,
  last_updated_by: getUserId(),
  inserted_by: getUserId()
})

const EditCreateIptvPiracy = ({ state, open, setOpen, record }) => {
  const [
    updateServicePiracy,
    { isLoading: isUpdating, isSuccess: SuccessUpdate, error: errorUpdate }
  ] = useUpdateIptvServerMutation()

  const [
    addServicePiracy,
    { isLoading: isAdding, isSuccess: SuccessAdd, error: errorAdd }
  ] = useCreateIptvServerMutation()

  const [markContentRemoved, { isLoading: isMarking }] =
    useMarkIptvContentRemovedMutation()

  const [fetchById, { isFetching: isRefetching }] =
    useLazyGetIptvServerByIdQuery()

  // { campaign_id, urls: [], selected: [] } when picking URLs to mark, else null
  const [markUrlModal, setMarkUrlModal] = useState(null)

  // fresh server data after a mark; feeds initialValues so enableReinitialize applies it
  const [liveRecord, setLiveRecord] = useState(null)

  // refetch server truth, then optionally strip the just-marked items
  // (idempotent: a no-op if the backend already removed them from the lists)
  const refreshModal = async (transform) => {
    try {
      const fresh = await fetchById({
        id: record?.id,
        expand: [
          'last_updated_by',
          'status',
          'iptv',
          'ip_address',
          'server_type',
          'customers',
          'real_isp_name',
          'channels',
          'campaigns'
        ].join(',')
      }).unwrap()
      if (fresh?.data) setLiveRecord(transform ? transform(fresh.data) : fresh.data)
    } catch {
      /* refetch failure is non-fatal: the mark already succeeded */
    }
  }

  const handleMarkChannel = async channel => {
    if (!channel.channel_id || !channel.url) {
      toast.error('Please select a channel and enter a URL first')
      return
    }
    try {
      const res = await markContentRemoved({
        id: record?.id,
        body: { channels: [{ channel_id: channel.channel_id, url: channel.url }] }
      }).unwrap()
      if (res?.success) {
        toast.success(res?.message)
        await refreshModal(data => ({
          ...data,
          channels: (data.channels || []).filter(
            c => !(c.channel_id === channel.channel_id && c.url === channel.url)
          )
        }))
      } else {
        toast.error(res?.message)
      }
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to mark content as removed')
    }
  }

  const submitCampaignMark = async (campaign_id, markedUrls) => {
    try {
      const res = await markContentRemoved({
        id: record?.id,
        body: { campaigns: [{ campaign_id, urls: markedUrls }] }
      }).unwrap()
      if (res?.success) {
        toast.success(res?.message)
        await refreshModal(data => ({
          ...data,
          campaigns: (data.campaigns || [])
            .map(c => {
              if (c.campaign_id !== campaign_id) return c
              const list = Array.isArray(c.urls)
                ? c.urls
                : String(c.urls || '').split('\n')
              return {
                ...c,
                urls: list.filter(u => !markedUrls.includes(u.trim()))
              }
            })
            // drop campaigns left with no URLs after marking
            .filter(c => (Array.isArray(c.urls) ? c.urls.length : c.urls))
        }))
      } else {
        toast.error(res?.message)
      }
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to mark content as removed')
    }
  }

  const handleMarkCampaign = campaign => {
    const urls = (campaign.urls || '').split('\n').map(u => u.trim()).filter(Boolean)
    if (!campaign.campaign_id || urls.length === 0) {
      toast.error('Please select a campaign and enter at least one URL first')
      return
    }
    if (urls.length === 1) {
      submitCampaignMark(campaign.campaign_id, urls)
      return
    }
    setMarkUrlModal({ campaign_id: campaign.campaign_id, urls, selected: [] })
  }

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: buildValues(liveRecord ?? record, state),

    validationSchema: Yup.object({
      iptv: Yup.number().required('Please Select IPTV App'),
      insertion_type: Yup.string().required('Please Select Insertion Type'),
      domain: Yup.string().when('insertion_type', {
        is: 'domain',
        then: schema => schema.required('Domain is required'),
        otherwise: schema => schema.nullable()
      }),
      ip_address: Yup.string()
        .required('IP Address is Required')
        .test('ip-address', 'Invalid IP Address', value => {
          return (
            value &&
            value.split('.').length === 4 &&
            value
              .split('.')
              .every(
                segment => segment >= 0 && segment <= 255 && segment !== ''
              )
          )
        }),
      customers: Yup.array()
        .min(1, 'Please Select at least one Customer')
        .required('Please Select Customers'),
      status: Yup.number().required('Please Select Status'),
      takedown_channels: Yup.boolean(),
      takedown_campaigns: Yup.boolean(),
      takedownCheck: Yup.mixed().test(
        'takedownCheck',
        'Please select at least one takedown type (Channels or Campaigns)',
        function () {
          return this.parent.takedown_channels || this.parent.takedown_campaigns
        }
      ),
      channels: Yup.array().when('takedown_channels', {
        is: true,
        then: schema =>
          schema
            .of(
              Yup.object().shape({
                channel_id: Yup.number()
                  .nullable()
                  .required('Please Select a Channel'),
                url: Yup.string().required('URL is required')
              })
            )
            .min(1, 'At least one channel is required'),
        otherwise: schema => schema.notRequired()
      }),
      campaigns: Yup.array().when('takedown_campaigns', {
        is: true,
        then: schema =>
          schema
            .of(
              Yup.object().shape({
                campaign_id: Yup.number()
                  .nullable()
                  .required('Please Select a Campaign'),
                urls: Yup.string().required('URLs are required')
              })
            )
            .min(1, 'At least one campaign is required'),
        otherwise: schema => schema.notRequired()
      }),
      extra_info: Yup.string().nullable()
    }),

    onSubmit: async values => {
      try {
        const payload = {
          insertion_type: values.insertion_type,
          domain: values.domain || '',
          ip_address: values.ip_address || '',
          iptv: values.iptv,
          status: values.status,
          customers: values.customers || [],
          takedown_channels: values.takedown_channels,
          takedown_campaigns: values.takedown_campaigns,
          channels: values.takedown_channels
            ? values.channels
                .filter(ch => ch.channel_id && ch.url)
                .map(ch => ({
                  channel_id: ch.channel_id,
                  url: ch.url
                }))
            : [],
          campaigns: values.takedown_campaigns
            ? values.campaigns
                .filter(camp => camp.campaign_id && camp.urls)
                .map(camp => ({
                  campaign_id: camp.campaign_id,
                  urls: camp.urls.split('\n').filter(url => url.trim())
                }))
            : [],
          real_isp_name: values.real_isp_name || null,
          extra_info: values.extra_info || '',
          last_updated_by: values.last_updated_by,
          inserted_by: values.inserted_by
        }

        if (state === 'edit') {
          let updateRes = await updateServicePiracy({
            body: payload,
            id: record?.id
          }).unwrap()
          if (updateRes?.success) {
            toast.success(updateRes?.message)
            setOpen(false)
          } else {
            toast.error(updateRes?.message)
          }
        } else {
          const createRes = await addServicePiracy(payload).unwrap()
          if (createRes?.success) {
            toast.success(createRes?.message)
            setOpen(false)
          } else {
            toast.error(createRes?.message)
          }
        }
      } catch (err) {
        toast.error(err?.data?.message || 'An error occurred while submitting')
      }
    }
  })

  // Mark button only makes sense when >1 content item exists; marking the only one would empty the server
  const channelCount = validation.values.takedown_channels
    ? validation.values.channels.filter(c => c.channel_id).length
    : 0
  const campaignCount = validation.values.takedown_campaigns
    ? validation.values.campaigns.filter(c => c.campaign_id).length
    : 0
  const canMark = state === 'edit' && !!record?.id && channelCount + campaignCount > 1

  const debouncedDomain = useDebounce(
    validation.values.insertion_type === 'domain'
      ? validation.values.domain
      : '',
    800
  )

  const { data: resolvedData, isFetching: isResolving } =
    useCalculateIpAddressQuery(
      { ip: debouncedDomain },
      {
        skip:
          !debouncedDomain ||
          validation.values.insertion_type !== 'domain' ||
          debouncedDomain.trim() === ''
      }
    )

  const [resolvedComparison, setResolvedComparison] = useState(null)
  useEffect(() => {
    if (!resolvedData?.data?.ip_address) return
    const newResolvedIP = resolvedData.data.ip_address
    const currentIP = validation.values.ip_address

    if (state === 'create') {
      if (!currentIP) {
        validation.setFieldValue('ip_address', newResolvedIP)
        setResolvedComparison(null)
      }
    } else if (state === 'edit') {
      if (newResolvedIP === currentIP) {
        setResolvedComparison({
          type: 'same',
          message:
            'The IP Address is same as last IP Address stored in the system.'
        })
      } else {
        setResolvedComparison({
          type: 'changed',
          message: `The new resolved IP Address is ${newResolvedIP}`
        })
      }
    }
  }, [resolvedData])

  useEffect(() => {
    const handleErrors = errObj => {
      if (errObj?.data) {
        if (errObj.data.message) {
          toast.error(errObj.data.message)
          return
        }
        const errorMessages = Object.values(errObj.data).flat()
        errorMessages.forEach(msg => toast.error(msg))
      }
    }
    handleErrors(errorAdd)
    handleErrors(errorUpdate)
  }, [errorAdd, errorUpdate])

  return (
    <Modal
      className='customModal'
      open={open}
      title={state === 'edit' ? 'Edit Piracy Server' : 'Create Piracy Server'}
      onCancel={() => setOpen(false)}
      footer={[
        <Button key='back' onClick={() => setOpen(false)}>
          Cancel
        </Button>,
        <Button
          key='submit'
          type='primary'
          onClick={async () => {
            const errors = await validation.validateForm()

            const touchedFields = {
              iptv: true,
              domain: true,
              ip_address: true,
              customers: true,
              status: true,
              insertion_type: true,
              takedown_channels: true,
              takedown_campaigns: true
            }

            if (
              validation.values.channels &&
              validation.values.channels.length > 0
            ) {
              touchedFields.channels = validation.values.channels.map(() => ({
                channel_id: true,
                url: true
              }))
            }

            if (
              validation.values.campaigns &&
              validation.values.campaigns.length > 0
            ) {
              touchedFields.campaigns = validation.values.campaigns.map(() => ({
                campaign_id: true,
                urls: true
              }))
            }

            validation.setTouched(touchedFields)

            if (Object.keys(errors).length !== 0) {
              const showErrors = (obj, prefix = '') => {
                Object.keys(obj).forEach(key => {
                  const error = obj[key]
                  if (typeof error === 'string') {
                    toast.error(error)
                  } else if (Array.isArray(error)) {
                    error.forEach((item, index) => {
                      if (typeof item === 'object' && item !== null) {
                        Object.keys(item).forEach(field => {
                          if (typeof item[field] === 'string') {
                            const fieldName =
                              key === 'channels' ? 'Channel' : 'Campaign'
                            toast.error(
                              `${fieldName} ${index + 1}: ${item[field]}`
                            )
                          }
                        })
                      }
                    })
                  } else if (typeof error === 'object' && error !== null) {
                    showErrors(error, key)
                  }
                })
              }
              showErrors(errors)
              return
            }
            validation.handleSubmit()
          }}
        >
          Submit
        </Button>
      ]}
    >
      <Loader isLoading={isAdding || isUpdating || isResolving || isMarking || isRefetching} />

      <div className='mb-3'>
        <IptvAppDropdown
          onChange={e => validation.setFieldValue('iptv', e)}
          value={validation.values.iptv}
          disabled={state === 'edit'}
        />
        {validation.touched.iptv && validation.errors.iptv && (
          <p className='text-danger'>{validation.errors.iptv}</p>
        )}
      </div>

      <div className='mb-3'>
        <CustomersDropdown
          onChange={e => {
            validation.setFieldValue('customers', e)
          }}
          value={validation.values.customers}
          mode='multiple'
        />
        {validation.touched.customers && validation.errors.customers && (
          <p className='text-danger'>{validation.errors.customers}</p>
        )}
      </div>

      <div className='mb-3'>
        <label>Insertion Type</label>
        <Select
          style={{ width: '100%' }}
          onChange={e => validation.setFieldValue('insertion_type', e)}
          value={validation.values.insertion_type}
          disabled={state === 'edit'}
          options={[
            { label: 'Domain', value: 'domain' },
            { label: 'IP Address', value: 'ip' }
          ]}
        />
        {validation.touched.insertion_type &&
          validation.errors.insertion_type && (
            <p className='text-danger'>{validation.errors.insertion_type}</p>
          )}
      </div>

      {validation.values.insertion_type === 'domain' && (
        <>
          <div className='mb-3'>
            <label>Domain</label>
            <Input
              type='text'
              name='domain'
              placeholder='example.com'
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.domain}
              disabled={state === 'edit'}
            />
            {validation.touched.domain && validation.errors.domain && (
              <p className='text-danger'>{validation.errors.domain}</p>
            )}
          </div>

          <div className='mb-3'>
            <label>
              IP Address {isResolving && <span>(resolving...)</span>}
            </label>
            <Input
              type='text'
              name='ip_address'
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.ip_address}
              placeholder='e.g., 192.168.1.1'
            />
            {validation.touched.ip_address && validation.errors.ip_address && (
              <p className='text-danger'>{validation.errors.ip_address}</p>
            )}

            {resolvedComparison && (
              <div
                className={`mt-2 px-3 py-2 rounded text-sm flex items-center justify-between ${
                  resolvedComparison.type === 'same'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                <span>{resolvedComparison.message}</span>
              </div>
            )}
          </div>
        </>
      )}

      {validation.values.insertion_type === 'ip' && (
        <div className='mb-3'>
          <label>IP Address</label>
          <Input
            type='text'
            name='ip_address'
            placeholder='e.g., 192.168.1.1'
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.ip_address}
          />
          {validation.touched.ip_address && validation.errors.ip_address && (
            <p className='text-danger'>{validation.errors.ip_address}</p>
          )}
        </div>
      )}

      <div className='mb-3'>
        <IspDropdown
          onChange={e => validation.setFieldValue('real_isp_name', e)}
          label='The Real ISP'
          value={validation.values.real_isp_name?.isp_name}
        />
      </div>

      <div className='mb-3'>
        <label>Takedown Contents</label>
        <div className='flex gap-3 mt-2'>
          <Checkbox
            checked={validation.values.takedown_channels}
            onChange={e =>
              validation.setFieldValue('takedown_channels', e.target.checked)
            }
          >
            Channels
          </Checkbox>
          <Checkbox
            checked={validation.values.takedown_campaigns}
            onChange={e =>
              validation.setFieldValue('takedown_campaigns', e.target.checked)
            }
          >
            Campaigns
          </Checkbox>
        </div>
        {validation.errors.takedownCheck && (
          <p className='text-danger'>{validation.errors.takedownCheck}</p>
        )}
      </div>
      {validation.values.takedown_channels && (
        <div
          className='mb-4'
          style={{
            padding: '16px',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}
        >
          <label
            className='mb-3 d-block'
            style={{ fontWeight: 600, fontSize: '14px' }}
          >
            Channels
          </label>
          {validation.values.channels.map((channel, index) => (
            <div
              key={index}
              className='mb-4 pb-3'
              style={{
                borderBottom:
                  index < validation.values.channels.length - 1
                    ? '1px solid #e5e7eb'
                    : 'none'
              }}
            >
              <div className='mb-3'>
                <CustomerChannelsDropdown
                  customer={validation.values?.customers}
                  onChange={e => {
                    const newChannels = [...validation.values.channels]
                    newChannels[index].channel_id = e
                    validation.setFieldValue('channels', newChannels)
                  }}
                  value={channel.channel_id}
                />
                {validation.touched.channels?.[index]?.channel_id &&
                  validation.errors.channels?.[index]?.channel_id && (
                    <p
                      className='text-danger'
                      style={{
                        fontSize: '12px',
                        marginTop: '4px',
                        marginBottom: 0
                      }}
                    >
                      {validation.errors.channels[index].channel_id}
                    </p>
                  )}
              </div>
              <div className='mb-3'>
                <label
                  style={{
                    fontSize: '12px',
                    marginBottom: '4px',
                    display: 'block',
                    color: '#6b7280'
                  }}
                >
                  URL
                </label>
                <Input
                  type='text'
                  placeholder='http://example.com/channel/stream.m3u8'
                  value={channel.url}
                  onChange={e => {
                    const newChannels = [...validation.values.channels]
                    newChannels[index].url = e.target.value
                    validation.setFieldValue('channels', newChannels)
                  }}
                />
                {validation.touched.channels?.[index]?.url &&
                  validation.errors.channels?.[index]?.url && (
                    <p
                      className='text-danger'
                      style={{
                        fontSize: '12px',
                        marginTop: '4px',
                        marginBottom: 0
                      }}
                    >
                      {validation.errors.channels[index].url}
                    </p>
                  )}
              </div>
              <div className='flex gap-2'>
                {validation.values.channels.length > 1 && (
                  <Button
                    danger
                    size='small'
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      const newChannels = validation.values.channels.filter(
                        (_, i) => i !== index
                      )
                      validation.setFieldValue('channels', newChannels)
                    }}
                  >
                    Remove Channel
                  </Button>
                )}
                {canMark && (
                  <Button
                    size='small'
                    loading={isMarking}
                    onClick={() => handleMarkChannel(channel)}
                  >
                    Mark as Content Removed
                  </Button>
                )}
              </div>
            </div>
          ))}
          <Button
            type='dashed'
            icon={<PlusOutlined />}
            onClick={() => {
              validation.setFieldValue('channels', [
                ...validation.values.channels,
                { channel_id: null, url: '' }
              ])
            }}
            style={{ width: '100%', marginTop: '8px' }}
          >
            Add Another Channel
          </Button>
          {validation.touched.channels &&
            validation.errors.channels &&
            typeof validation.errors.channels === 'string' && (
              <p className='text-danger mt-2' style={{ marginBottom: 0 }}>
                {validation.errors.channels}
              </p>
            )}
        </div>
      )}
      {validation.values.takedown_campaigns && (
        <div
          className='mb-4'
          style={{
            padding: '16px',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}
        >
          <label
            className='mb-3 d-block'
            style={{ fontWeight: 600, fontSize: '14px' }}
          >
            Campaigns
          </label>
          {validation.values.campaigns.map((campaign, index) => (
            <div
              key={index}
              className='mb-4 pb-3'
              style={{
                borderBottom:
                  index < validation.values.campaigns.length - 1
                    ? '1px solid #e5e7eb'
                    : 'none'
              }}
            >
              <div className='mb-3'>
                <CampaignDropdown
                  customer_id={validation.values?.customers}
                  onChange={e => {
                    const newCampaigns = [...validation.values.campaigns]
                    newCampaigns[index].campaign_id = e
                    validation.setFieldValue('campaigns', newCampaigns)
                  }}
                  value={campaign.campaign_id}
                />
                {validation.touched.campaigns?.[index]?.campaign_id &&
                  validation.errors.campaigns?.[index]?.campaign_id && (
                    <p
                      className='text-danger'
                      style={{
                        fontSize: '12px',
                        marginTop: '4px',
                        marginBottom: 0
                      }}
                    >
                      {validation.errors.campaigns[index].campaign_id}
                    </p>
                  )}
              </div>
              <div className='mb-3'>
                <label
                  style={{
                    fontSize: '12px',
                    marginBottom: '4px',
                    display: 'block',
                    color: '#6b7280'
                  }}
                >
                  URLs (one per line)
                </label>
                <TextArea
                  rows={3}
                  placeholder='http://example.com/campaign1/stream1.m3u8&#10;http://example.com/campaign1/stream2.m3u8&#10;http://example.com/campaign1/stream3.m3u8'
                  value={campaign.urls}
                  onChange={e => {
                    const newCampaigns = [...validation.values.campaigns]
                    newCampaigns[index].urls = e.target.value
                    validation.setFieldValue('campaigns', newCampaigns)
                  }}
                />
                {validation.touched.campaigns?.[index]?.urls &&
                  validation.errors.campaigns?.[index]?.urls && (
                    <p
                      className='text-danger'
                      style={{
                        fontSize: '12px',
                        marginTop: '4px',
                        marginBottom: 0
                      }}
                    >
                      {validation.errors.campaigns[index].urls}
                    </p>
                  )}
              </div>
              <div className='flex gap-2'>
                {validation.values.campaigns.length > 1 && (
                  <Button
                    danger
                    size='small'
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      const newCampaigns = validation.values.campaigns.filter(
                        (_, i) => i !== index
                      )
                      validation.setFieldValue('campaigns', newCampaigns)
                    }}
                  >
                    Remove Campaign
                  </Button>
                )}
                {canMark && (
                  <Button
                    size='small'
                    loading={isMarking}
                    onClick={() => handleMarkCampaign(campaign)}
                  >
                    Mark as Content Removed
                  </Button>
                )}
              </div>
            </div>
          ))}
          <Button
            type='dashed'
            icon={<PlusOutlined />}
            onClick={() => {
              validation.setFieldValue('campaigns', [
                ...validation.values.campaigns,
                { campaign_id: null, urls: '' }
              ])
            }}
            style={{ width: '100%', marginTop: '8px' }}
          >
            Add Another Campaign
          </Button>
          {validation.touched.campaigns &&
            validation.errors.campaigns &&
            typeof validation.errors.campaigns === 'string' && (
              <p className='text-danger mt-2' style={{ marginBottom: 0 }}>
                {validation.errors.campaigns}
              </p>
            )}
        </div>
      )}

      <div className='mb-3'>
        <label>Extra Info</label>
        <TextArea
          rows={4}
          name='extra_info'
          value={validation.values.extra_info}
          onChange={validation.handleChange}
        />
      </div>

      {/* Status */}
      <div className='mb-3'>
        <IptvStatusDropdown
          label='Status'
          onChange={e => validation.setFieldValue('status', e)}
          value={validation.values.status}
        />
      </div>

      {markUrlModal && (
        <Modal
          open
          title='Select URLs to mark as Content Removed'
          okText='Mark Selected'
          okButtonProps={{ disabled: !markUrlModal.selected.length }}
          confirmLoading={isMarking}
          onCancel={() => setMarkUrlModal(null)}
          onOk={async () => {
            await submitCampaignMark(markUrlModal.campaign_id, markUrlModal.selected)
            setMarkUrlModal(null)
          }}
        >
          <Checkbox
            checked={markUrlModal.selected.length === markUrlModal.urls.length}
            indeterminate={
              markUrlModal.selected.length > 0 &&
              markUrlModal.selected.length < markUrlModal.urls.length
            }
            onChange={e =>
              setMarkUrlModal(prev => ({
                ...prev,
                selected: e.target.checked ? prev.urls : []
              }))
            }
            style={{ marginBottom: 12, fontWeight: 500 }}
          >
            Select All
          </Checkbox>
          <Checkbox.Group
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            value={markUrlModal.selected}
            onChange={selected =>
              setMarkUrlModal(prev => ({ ...prev, selected }))
            }
            options={markUrlModal.urls.map(u => ({ label: u, value: u }))}
          />
        </Modal>
      )}
    </Modal>
  )
}

export default EditCreateIptvPiracy
